
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { lumi } from '../lib/lumi'
import {Upload, X, Plus, Trash2, Save, LogOut, Settings as SettingsIcon, GripVertical} from 'lucide-react'
import ConfigTab from '../components/ConfigTab'

interface Project {
  _id?: string
  name: string
  description: string
  note?: string
  logoUrl?: string
  websiteUrl: string
  resourceUrl?: string
  status: 'active' | 'completed' | 'inactive'
  displayOrder: number
}

interface Location {
  _id?: string
  name: string
  year: number
  imageUrl: string
  description?: string
}

interface User {
  projectId: string
  userId: string
  email: string
  userName: string
  userRole: 'ADMIN' | 'USER'
  createdTime: string
  accessToken: string
  // 添加可能的其他字段
  role?: string
  name?: string
  [key: string]: any
}

interface SiteConfig {
  _id?: string
  configKey: string
  configValue: string
  description: string
}

export default function Settings() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'projects' | 'locations' | 'config'>('projects')
  
  // Projects state
  const [projects, setProjects] = useState<Project[]>([])
  const [editingProject, setEditingProject] = useState<Project | null>(null)
  const [isAddingProject, setIsAddingProject] = useState(false)
  
  // Locations state
  const [locations, setLocations] = useState<Location[]>([])
  const [editingLocation, setEditingLocation] = useState<Location | null>(null)
  const [isAddingLocation, setIsAddingLocation] = useState(false)
  
  // Site Config state
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null)
  const [editingConfig, setEditingConfig] = useState(false)
  const [tempUpdateDate, setTempUpdateDate] = useState('')
  
  // Upload state
  const [uploading, setUploading] = useState(false)

  // 获取用户角色的辅助函数 - 修复嵌套访问
  const getUserRole = (userObj: any): string => {
    // 首先检查是否有嵌套的user对象
    const actualUser = userObj?.user || userObj
    
    // 尝试不同的角色字段名
    return actualUser?.userRole || actualUser?.role || actualUser?.Role || actualUser?.USER_ROLE || 'USER'
  }

  // 获取用户名的辅助函数 - 修复嵌套访问
  const getUserName = (userObj: any): string => {
    // 首先检查是否有嵌套的user对象
    const actualUser = userObj?.user || userObj
    
    return actualUser?.userName || actualUser?.name || actualUser?.username || actualUser?.displayName || actualUser?.email || '未知用户'
  }

  // 获取用户ID的辅助函数
  const getUserId = (userObj: any): string => {
    const actualUser = userObj?.user || userObj
    return actualUser?.userId || actualUser?.id || actualUser?.user_id || '未知'
  }

  // 获取用户邮箱的辅助函数
  const getUserEmail = (userObj: any): string => {
    const actualUser = userObj?.user || userObj
    return actualUser?.email || actualUser?.emailAddress || '未知'
  }

  // 检查是否为管理员
  const isAdmin = (userObj: any): boolean => {
    const role = getUserRole(userObj)
    return role?.toUpperCase() === 'ADMIN' || role?.toUpperCase() === 'ADMINISTRATOR'
  }

  // Check authentication and role on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('🔍 开始检查认证状态...')
        
        // 获取当前用户信息
        const currentUser = lumi.auth.user
        const isAuthenticated = lumi.auth.isAuthenticated
        
        console.log('📋 认证检查结果:', { 
          currentUser, 
          isAuthenticated,
          userKeys: currentUser ? Object.keys(currentUser) : null,
          userType: typeof currentUser,
          hasNestedUser: !!(currentUser?.user)
        })

        // 深入检查用户对象的所有属性
        if (currentUser) {
          console.log('🔎 用户对象深度分析:')
          console.log('- 顶层对象:', currentUser)
          console.log('- 顶层键:', Object.keys(currentUser))
          
          // 检查嵌套的用户信息
          if (currentUser.user) {
            console.log('- 🎯 找到嵌套用户对象:', currentUser.user)
            console.log('- 嵌套用户键:', Object.keys(currentUser.user))
            console.log('- 嵌套用户完整信息:', JSON.stringify(currentUser.user, null, 2))
          }
          
          const detectedRole = getUserRole(currentUser)
          const detectedName = getUserName(currentUser)
          const detectedUserId = getUserId(currentUser)
          const detectedEmail = getUserEmail(currentUser)
          const adminStatus = isAdmin(currentUser)
          
          console.log('🎯 解析结果:')
          console.log('- 检测到的角色:', detectedRole)
          console.log('- 检测到的用户名:', detectedName)
          console.log('- 检测到的用户ID:', detectedUserId)
          console.log('- 检测到的邮箱:', detectedEmail)
          console.log('- 是否为管理员:', adminStatus)
        }
        
        if (isAuthenticated && currentUser) {
          console.log('✅ 用户已认证，设置用户信息')
          
          // 创建标准化的用户对象
          const standardizedUser = {
            ...currentUser,
            userRole: getUserRole(currentUser),
            userName: getUserName(currentUser),
            userId: getUserId(currentUser),
            email: getUserEmail(currentUser),
            // 保留原始数据以备调试
            _original: currentUser
          }
          
          setUser(standardizedUser as User)
          
          // 检查用户角色
          const userRole = getUserRole(currentUser)
          console.log('🔐 最终角色检查:', userRole)
          
          if (isAdmin(currentUser)) {
            console.log('👑 确认用户是管理员，开始获取数据')
            await fetchData()
          } else {
            console.log('🚫 用户不是管理员，角色为:', userRole)
          }
        } else {
          console.log('❌ 用户未认证或用户信息为空')
          setUser(null)
        }
      } catch (error) {
        console.error('💥 认证检查失败:', error)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    // 初始检查
    checkAuth()

    // 监听认证状态变化
    const unsubscribe = lumi.auth.onAuthChange((newUser) => {
      console.log('🔄 认证状态变化:', { 
        newUser,
        hasUser: !!newUser,
        userKeys: newUser ? Object.keys(newUser) : null,
        hasNestedUser: !!(newUser?.user)
      })
      
      if (newUser) {
        console.log('📝 新用户详细信息:', JSON.stringify(newUser, null, 2))
        
        const detectedRole = getUserRole(newUser)
        const detectedName = getUserName(newUser)
        const detectedUserId = getUserId(newUser)
        const detectedEmail = getUserEmail(newUser)
        const adminStatus = isAdmin(newUser)
        
        console.log('🎯 新用户解析结果:')
        console.log('- 角色:', detectedRole)
        console.log('- 用户名:', detectedName)
        console.log('- 用户ID:', detectedUserId)
        console.log('- 邮箱:', detectedEmail)
        console.log('- 管理员状态:', adminStatus)
        
        // 创建标准化的用户对象
        const standardizedUser = {
          ...newUser,
          userRole: detectedRole,
          userName: detectedName,
          userId: detectedUserId,
          email: detectedEmail,
          _original: newUser
        }
        
        setUser(standardizedUser as User)
        
        if (adminStatus) {
          console.log('👑 新用户是管理员，获取数据')
          fetchData()
        }
      } else {
        console.log('👋 用户登出')
        setUser(null)
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  // Fetch data
  const fetchData = async () => {
    try {
      console.log('📊 开始获取项目和地点数据...')
      
      // Fetch projects
      const { list: projectList } = await lumi.entities.projects.list({
        sort: { displayOrder: 1 }
      })
      console.log('📁 获取到项目数据:', projectList?.length || 0, '个项目')
      setProjects(projectList as unknown as Project[])

      // Fetch locations
      const { list: locationList } = await lumi.entities.locations.list({
        sort: { year: -1, createdAt: -1 }
      })
      console.log('📍 获取到地点数据:', locationList?.length || 0, '个地点')
      setLocations(locationList as unknown as Location[])
      
      // Fetch site config
      const { list: configList } = await lumi.entities.siteConfig.list({
        filter: { configKey: 'lastUpdateDate' }
      })
      console.log('⚙️ 获取到配置数据:', configList?.length || 0, '个配置')
      if (configList && configList.length > 0) {
        setSiteConfig(configList[0] as unknown as SiteConfig)
      } else {
        // 初始化配置数据
        console.log('🔧 配置数据为空，开始初始化...')
        try {
          const newConfig = await lumi.entities.siteConfig.create({
            configKey: 'lastUpdateDate',
            configValue: '2025.8.1',
            description: '站点最后更新日期',
            creator: 'system',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })
          console.log('✅ 配置初始化成功:', newConfig)
          setSiteConfig(newConfig as unknown as SiteConfig)
        } catch (initError) {
          console.error('❌ 配置初始化失败:', initError)
        }
      }
    } catch (error) {
      console.error('💥 获取数据失败:', error)
    }
  }

  // Handle login
  const handleLogin = async () => {
    try {
      console.log('🔑 尝试登录...')
      await lumi.auth.signIn()
      console.log('📤 登录请求已发送')
    } catch (error) {
      console.error('💥 登录失败:', error)
    }
  }

  // Handle logout
  const handleLogout = async () => {
    try {
      console.log('👋 尝试登出...')
      await lumi.auth.signOut()
      console.log('✅ 登出成功')
    } catch (error) {
      console.error('💥 登出失败:', error)
    }
  }

  // Handle file upload
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return null

    setUploading(true)
    try {
      const fileArray = Array.from(files)
      const results = await lumi.tools.file.upload(fileArray)
      
      if (results[0]?.fileUrl) {
        return results[0].fileUrl
      } else {
        console.error('文件上传失败:', results[0]?.uploadError)
        return null
      }
    } catch (error) {
      console.error('文件上传失败:', error)
      return null
    } finally {
      setUploading(false)
    }
  }

  // Project operations
  const handleSaveProject = async (project: Project) => {
    try {
      if (project._id) {
        // Update existing project
        await lumi.entities.projects.update(project._id, {
          name: project.name,
          description: project.description,
          note: project.note,
          logoUrl: project.logoUrl,
          websiteUrl: project.websiteUrl,
          resourceUrl: project.resourceUrl,
          status: project.status,
          displayOrder: project.displayOrder
        })
      } else {
        // Create new project
        await lumi.entities.projects.create({
          name: project.name,
          description: project.description,
          note: project.note,
          logoUrl: project.logoUrl,
          websiteUrl: project.websiteUrl,
          resourceUrl: project.resourceUrl,
          status: project.status,
          displayOrder: project.displayOrder,
          creator: getUserName(user) || 'admin'
        })
      }
      
      setEditingProject(null)
      setIsAddingProject(false)
      await fetchData()
    } catch (error) {
      console.error('保存项目失败:', error)
    }
  }

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('确定要删除这个项目吗？')) return
    
    try {
      await lumi.entities.projects.delete(projectId)
      await fetchData()
    } catch (error) {
      console.error('删除项目失败:', error)
    }
  }

  // 拖拽重排：对指定分组内的项目重写 displayOrder（从 1 开始）并批量保存
  const handleReorderProjects = async (orderedGroup: Project[]) => {
    try {
      // 乐观更新：先在本地更新该组项目的 displayOrder
      const idToOrder = new Map<string, number>()
      orderedGroup.forEach((p, index) => {
        if (p._id) idToOrder.set(p._id, index + 1)
      })
      setProjects((prev) =>
        prev.map((p) =>
          p._id && idToOrder.has(p._id)
            ? { ...p, displayOrder: idToOrder.get(p._id) as number }
            : p
        )
      )

      // 批量持久化（并行）
      await Promise.all(
        orderedGroup.map((p, index) =>
          p._id
            ? lumi.entities.projects.update(p._id, { displayOrder: index + 1 })
            : Promise.resolve()
        )
      )
      console.log('✅ 项目排序已保存')
      await fetchData()
    } catch (error) {
      console.error('保存排序失败:', error)
      alert('保存排序失败')
      await fetchData()
    }
  }

  // 复制项目：进行中 <-> 过往（互相复制为另一组）
  const handleCopyProject = async (project: Project) => {
    // 进行中（active/completed）复制为过往（inactive）；过往复制为进行中（active）
    const isPrevious = project.status === 'inactive'
    const targetStatus: Project['status'] = isPrevious ? 'active' : 'inactive'
    const targetLabel = isPrevious ? '进行中项目' : '过往项目'

    if (!confirm(`确定要将「${project.name}」复制为${targetLabel}吗？`)) return

    try {
      // 计算目标分组当前数量，新副本排到该组末尾
      const targetGroupCount =
        targetStatus === 'inactive'
          ? projects.filter((p) => p.status === 'inactive').length
          : projects.filter((p) => p.status === 'active' || p.status === 'completed').length

      await lumi.entities.projects.create({
        name: project.name,
        description: project.description,
        note: project.note,
        logoUrl: project.logoUrl,
        websiteUrl: project.websiteUrl,
        resourceUrl: project.resourceUrl,
        status: targetStatus,
        displayOrder: targetGroupCount + 1,
        creator: getUserName(user) || 'admin'
      })
      await fetchData()
    } catch (error) {
      console.error('复制项目失败:', error)
      alert('复制项目失败')
    }
  }

  // Auto update last update date helper
  const autoUpdateLastUpdateDate = async () => {
    try {
      const today = new Date().toISOString().split('T')[0].replace(/-/g, '.')
      
      if (siteConfig?._id) {
        await lumi.entities.siteConfig.update(siteConfig._id, {
          configValue: today,
          description: '站点最后更新日期（自动更新）'
        })
        console.log('✅ 自动更新日期为:', today)
      } else {
        const newConfig = await lumi.entities.siteConfig.create({
          configKey: 'lastUpdateDate',
          configValue: today,
          description: '站点最后更新日期（自动更新）',
          creator: getUserName(user) || 'admin'
        })
        setSiteConfig(newConfig as unknown as SiteConfig)
        console.log('✅ 创建并设置日期为:', today)
      }
    } catch (error) {
      console.error('自动更新日期失败:', error)
    }
  }
  
  // Location operations
  const handleSaveLocation = async (location: Location) => {
    try {
      if (location._id) {
        // Update existing location
        await lumi.entities.locations.update(location._id, {
          name: location.name,
          year: location.year,
          imageUrl: location.imageUrl,
          description: location.description
        })
      } else {
        // Create new location
        await lumi.entities.locations.create({
          name: location.name,
          year: location.year,
          imageUrl: location.imageUrl,
          description: location.description,
          creator: getUserName(user) || 'admin'
        })
      }
      
      // Auto update last update date
      await autoUpdateLastUpdateDate()
      
      setEditingLocation(null)
      setIsAddingLocation(false)
      await fetchData()
    } catch (error) {
      console.error('保存地点失败:', error)
    }
  }

  const handleDeleteLocation = async (locationId: string) => {
    if (!confirm('确定要删除这个地点吗？')) return
    
    try {
      await lumi.entities.locations.delete(locationId)
      
      // Auto update last update date
      await autoUpdateLastUpdateDate()
      
      await fetchData()
    } catch (error) {
      console.error('删除地点失败:', error)
    }
  }
  
  // Config operations
  const handleUpdateConfig = async () => {
    if (!tempUpdateDate) {
      alert('请输入日期')
      return
    }
    
    try {
      if (siteConfig?._id) {
        await lumi.entities.siteConfig.update(siteConfig._id, {
          configValue: tempUpdateDate,
          description: '站点最后更新日期（手动更新）'
        })
      } else {
        const newConfig = await lumi.entities.siteConfig.create({
          configKey: 'lastUpdateDate',
          configValue: tempUpdateDate,
          description: '站点最后更新日期（手动更新）',
          creator: getUserName(user) || 'admin'
        })
        setSiteConfig(newConfig as unknown as SiteConfig)
      }
      
      setEditingConfig(false)
      await fetchData()
      alert('更新日期成功')
    } catch (error) {
      console.error('更新配置失败:', error)
      alert('更新日期失败')
    }
  }
  
  const handleSetToToday = async () => {
    const today = new Date().toISOString().split('T')[0].replace(/-/g, '.')
    setTempUpdateDate(today)
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-2xl font-mono">
          <div className="mb-4">加载中...</div>
          <div className="text-sm text-gray-500">
            正在检查用户认证状态
          </div>
        </div>
      </div>
    )
  }

  // Not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8 border-4 border-black">
          <SettingsIcon className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-3xl font-black mb-4">管理员设置</h1>
          <p className="text-lg mb-6 text-gray-600">
            请登录以访问管理员设置页面
          </p>
          <div className="mb-4 text-sm text-gray-500">
            <div>认证状态: 未登录</div>
            <div>用户信息: 无</div>
          </div>
          <button
            onClick={handleLogin}
            className="bg-black text-white px-8 py-4 text-lg font-bold hover:bg-gray-800 transition-colors"
          >
            登录
          </button>
        </div>
      </div>
    )
  }

  // Not admin
  if (!isAdmin(user)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-2xl mx-auto text-center p-8 border-4 border-black">
          <SettingsIcon className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-3xl font-black mb-4">访问受限</h1>
          
          <div className="bg-gray-100 p-6 mb-6 text-left">
            <h3 className="font-bold mb-4 text-lg">🔍 调试信息</h3>
            <div className="space-y-2 text-sm font-mono">
              <div><span className="text-gray-600">当前用户:</span> <span className="font-bold">{getUserName(user)}</span></div>
              <div><span className="text-gray-600">检测角色:</span> <span className="font-bold">{getUserRole(user)}</span></div>
              <div><span className="text-gray-600">管理员状态:</span> <span className="font-bold">{isAdmin(user) ? '是' : '否'}</span></div>
              <div><span className="text-gray-600">用户ID:</span> <span className="font-bold">{getUserId(user)}</span></div>
              <div><span className="text-gray-600">邮箱:</span> <span className="font-bold">{getUserEmail(user)}</span></div>
              <div className="mt-4">
                <span className="text-gray-600">是否有嵌套用户对象:</span>
                <div className="text-xs text-gray-500 mt-1">
                  {user._original?.user ? '是 (user.user存在)' : '否'}
                </div>
              </div>
              <div className="mt-2">
                <span className="text-gray-600">原始用户对象键:</span>
                <div className="text-xs text-gray-500 mt-1">
                  {user._original ? Object.keys(user._original).join(', ') : '无'}
                </div>
              </div>
              {user._original?.user && (
                <div className="mt-2">
                  <span className="text-gray-600">嵌套用户对象键:</span>
                  <div className="text-xs text-gray-500 mt-1">
                    {Object.keys(user._original.user).join(', ')}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <p className="text-lg mb-6 text-red-600">
            此页面仅限管理员访问
          </p>
          <p className="text-sm mb-6 text-gray-600">
            如果您应该是管理员，请联系系统管理员检查您的账户角色设置
          </p>
          
          <button
            onClick={handleLogout}
            className="bg-black text-white px-8 py-4 text-lg font-bold hover:bg-gray-800 transition-colors"
          >
            切换账户
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white font-mono">
      {/* Header */}
      <header className="bg-black text-white p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-black">管理员设置</h1>
            <p className="text-lg">
              欢迎, {getUserName(user)} ({getUserRole(user)})
            </p>
            <div className="text-sm text-gray-300">
              用户ID: {getUserId(user)} | 邮箱: {getUserEmail(user)}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 bg-white text-black px-6 py-3 font-bold hover:bg-gray-200 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>登出</span>
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="border-b-4 border-black">
        <div className="max-w-7xl mx-auto">
          <div className="flex">
            <button
              onClick={() => setActiveTab('projects')}
              className={`px-8 py-4 text-xl font-bold border-r-2 border-black transition-colors ${
                activeTab === 'projects'
                  ? 'bg-black text-white'
                  : 'bg-white text-black hover:bg-gray-100'
              }`}
            >
              项目管理
            </button>
            <button
              onClick={() => setActiveTab('locations')}
              className={`px-8 py-4 text-xl font-bold border-r-2 border-black transition-colors ${
                activeTab === 'locations'
                  ? 'bg-black text-white'
                  : 'bg-white text-black hover:bg-gray-100'
              }`}
            >
              地点管理
            </button>
            <button
              onClick={() => setActiveTab('config')}
              className={`px-8 py-4 text-xl font-bold transition-colors ${
                activeTab === 'config'
                  ? 'bg-black text-white'
                  : 'bg-white text-black hover:bg-gray-100'
              }`}
            >
              站点配置
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {activeTab === 'projects' && (
          <ProjectsTab
            projects={projects}
            editingProject={editingProject}
            setEditingProject={setEditingProject}
            isAddingProject={isAddingProject}
            setIsAddingProject={setIsAddingProject}
            onSave={handleSaveProject}
            onDelete={handleDeleteProject}
            onCopy={handleCopyProject}
            onReorder={handleReorderProjects}
            onFileUpload={handleFileUpload}
            uploading={uploading}
          />
        )}

        {activeTab === 'locations' && (
          <LocationsTab
            locations={locations}
            editingLocation={editingLocation}
            setEditingLocation={setEditingLocation}
            isAddingLocation={isAddingLocation}
            setIsAddingLocation={setIsAddingLocation}
            onSave={handleSaveLocation}
            onDelete={handleDeleteLocation}
            onFileUpload={handleFileUpload}
            uploading={uploading}
          />
        )}
        
        {activeTab === 'config' && (
          <ConfigTab
            siteConfig={siteConfig}
            editingConfig={editingConfig}
            setEditingConfig={setEditingConfig}
            tempUpdateDate={tempUpdateDate}
            setTempUpdateDate={setTempUpdateDate}
            onUpdate={handleUpdateConfig}
            onSetToday={handleSetToToday}
          />
        )}
      </div>
    </div>
  )
}

// Projects Tab Component
function ProjectsTab({
  projects,
  editingProject,
  setEditingProject,
  isAddingProject,
  setIsAddingProject,
  onSave,
  onDelete,
  onCopy,
  onReorder,
  onFileUpload,
  uploading
}: {
  projects: Project[]
  editingProject: Project | null
  setEditingProject: (project: Project | null) => void
  isAddingProject: boolean
  setIsAddingProject: (adding: boolean) => void
  onSave: (project: Project) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onCopy: (project: Project) => Promise<void>
  onReorder: (orderedGroup: Project[]) => Promise<void>
  onFileUpload: (files: FileList | null) => Promise<string | null>
  uploading: boolean
}) {
  // 按状态分组：WIP（active/completed）与 Previous（inactive），各自按 displayOrder 排序
  const wipProjects = projects
    .filter((p) => p.status === 'active' || p.status === 'completed')
    .sort((a, b) => a.displayOrder - b.displayOrder)
  const previousProjects = projects
    .filter((p) => p.status === 'inactive')
    .sort((a, b) => a.displayOrder - b.displayOrder)

  // 添加进行中项目（默认 active，排序插到 WIP 组末尾）
  const handleAddWip = () => {
    setIsAddingProject(true)
    setEditingProject({
      name: '',
      description: '',
      logoUrl: '',
      websiteUrl: '',
      resourceUrl: '',
      status: 'active',
      displayOrder: wipProjects.length + 1
    })
  }

  // 添加过往项目（固定 inactive，排序插到过往组末尾）
  const handleAddPrevious = () => {
    setIsAddingProject(true)
    setEditingProject({
      name: '',
      description: '',
      logoUrl: '',
      websiteUrl: '',
      resourceUrl: '',
      status: 'inactive',
      displayOrder: previousProjects.length + 1
    })
  }

  return (
    <div>
      {/* WIP Projects (进行中项目: active / completed) */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <h2 className="text-2xl font-black border-b-4 border-black pb-1">进行中项目 (Projects WIP)</h2>
            <span className="ml-3 text-sm text-gray-500">
              状态为「进行中 / 已完成」，{wipProjects.length} 个
            </span>
          </div>
          <button
            onClick={handleAddWip}
            className="flex items-center space-x-2 bg-black text-white px-5 py-2.5 font-bold hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>添加进行中项目</span>
          </button>
        </div>
        {wipProjects.length > 0 ? (
          <DraggableProjectList
            items={wipProjects}
            gridClass="grid grid-cols-1 lg:grid-cols-2 gap-6"
            onEdit={setEditingProject}
            onDelete={onDelete}
            onCopy={onCopy}
            onReorder={onReorder}
          />
        ) : (
          <p className="text-gray-400 border-2 border-dashed border-gray-300 p-6">暂无进行中项目</p>
        )}
      </div>

      {/* Previous Projects (过往项目: inactive) */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <h2 className="text-2xl font-black border-b-4 border-black pb-1">过往项目 (Previous Projects)</h2>
            <span className="ml-3 text-sm text-gray-500">
              {previousProjects.length} 个
            </span>
          </div>
          <button
            onClick={handleAddPrevious}
            className="flex items-center space-x-2 bg-black text-white px-5 py-2.5 font-bold hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>添加过往项目</span>
          </button>
        </div>
        {previousProjects.length > 0 ? (
          <DraggableProjectList
            items={previousProjects}
            gridClass="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            onEdit={setEditingProject}
            onDelete={onDelete}
            onCopy={onCopy}
            onReorder={onReorder}
          />
        ) : (
          <p className="text-gray-400 border-2 border-dashed border-gray-300 p-6">暂无过往项目</p>
        )}
      </div>

      {/* Edit Modal */}
      {(editingProject || isAddingProject) && (
        <ProjectEditModal
          project={editingProject}
          onSave={onSave}
          onCancel={() => {
            setEditingProject(null)
            setIsAddingProject(false)
          }}
          onFileUpload={onFileUpload}
          uploading={uploading}
        />
      )}
    </div>
  )
}

// Locations Tab Component
function LocationsTab({
  locations,
  editingLocation,
  setEditingLocation,
  isAddingLocation,
  setIsAddingLocation,
  onSave,
  onDelete,
  onFileUpload,
  uploading
}: {
  locations: Location[]
  editingLocation: Location | null
  setEditingLocation: (location: Location | null) => void
  isAddingLocation: boolean
  setIsAddingLocation: (adding: boolean) => void
  onSave: (location: Location) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onFileUpload: (files: FileList | null) => Promise<string | null>
  uploading: boolean
}) {
  return (
    <div>
      {/* Add Button */}
      <div className="mb-6">
        <button
          onClick={() => {
            setIsAddingLocation(true)
            setEditingLocation({
              name: '',
              year: new Date().getFullYear(),
              imageUrl: '',
              description: ''
            })
          }}
          className="flex items-center space-x-2 bg-black text-white px-6 py-3 font-bold hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>添加地点</span>
        </button>
      </div>

      {/* Location List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {locations.map((location) => (
          <LocationCard
            key={location._id}
            location={location}
            onEdit={setEditingLocation}
            onDelete={onDelete}
          />
        ))}
      </div>

      {/* Edit Modal */}
      {(editingLocation || isAddingLocation) && (
        <LocationEditModal
          location={editingLocation}
          onSave={onSave}
          onCancel={() => {
            setEditingLocation(null)
            setIsAddingLocation(false)
          }}
          onFileUpload={onFileUpload}
          uploading={uploading}
        />
      )}
    </div>
  )
}

// Draggable Project List Component（同组内拖拽排序）
function DraggableProjectList({
  items,
  gridClass,
  onEdit,
  onDelete,
  onCopy,
  onReorder
}: {
  items: Project[]
  gridClass: string
  onEdit: (project: Project) => void
  onDelete: (id: string) => Promise<void>
  onCopy: (project: Project) => Promise<void>
  onReorder: (orderedGroup: Project[]) => Promise<void>
}) {
  const [orderedItems, setOrderedItems] = useState<Project[]>(items)
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)

  // 外部数据更新时同步本地顺序
  useEffect(() => {
    setOrderedItems(items)
  }, [items])

  const handleDragStart = (index: number) => {
    setDragIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (index !== overIndex) setOverIndex(index)
  }

  const handleDrop = async (index: number) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null)
      setOverIndex(null)
      return
    }
    const next = [...orderedItems]
    const [moved] = next.splice(dragIndex, 1)
    next.splice(index, 0, moved)
    setOrderedItems(next)
    setDragIndex(null)
    setOverIndex(null)
    await onReorder(next)
  }

  const handleDragEnd = () => {
    setDragIndex(null)
    setOverIndex(null)
  }

  return (
    <div className={gridClass}>
      {orderedItems.map((project, index) => (
        <div
          key={project._id}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={() => handleDrop(index)}
          onDragEnd={handleDragEnd}
          className={`transition-opacity ${
            dragIndex === index ? 'opacity-40' : ''
          } ${overIndex === index && dragIndex !== index ? 'ring-4 ring-black/40' : ''}`}
        >
          <ProjectCard
            project={project}
            onEdit={onEdit}
            onDelete={onDelete}
            onCopy={onCopy}
          />
        </div>
      ))}
    </div>
  )
}

// Project Card Component
function ProjectCard({
  project,
  onEdit,
  onDelete,
  onCopy
}: {
  project: Project
  onEdit: (project: Project) => void
  onDelete: (id: string) => Promise<void>
  onCopy: (project: Project) => Promise<void>
}) {
  const isPrevious = project.status === 'inactive'
  const copyLabel = isPrevious ? '复制为进行中' : '复制为过往'
  return (
    <div className="border-4 border-black p-6 bg-white h-full">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center text-gray-400 mb-2 cursor-grab active:cursor-grabbing select-none" title="拖拽以排序">
            <GripVertical className="w-5 h-5" />
            <span className="ml-1 text-xs">拖拽排序</span>
          </div>
          <h3 className="text-xl font-bold mb-2">{project.name}</h3>
          <p className="text-gray-600 mb-2">{project.description}</p>
          <div className="text-sm text-gray-500">
            <div>状态: {project.status}</div>
            <div>排序: {project.displayOrder}</div>
          </div>
        </div>
        {project.logoUrl && (
          <img
            src={project.logoUrl}
            alt={project.name}
            className="w-16 h-16 object-cover border-2 border-black ml-4"
          />
        )}
      </div>
      
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => onEdit(project)}
          className="bg-black text-white px-4 py-2 font-bold hover:bg-gray-800 transition-colors"
        >
          编辑
        </button>
        <button
          onClick={() => onCopy(project)}
          className="border-2 border-black bg-white text-black px-4 py-2 font-bold hover:bg-gray-100 transition-colors"
        >
          {copyLabel}
        </button>
        <button
          onClick={() => project._id && onDelete(project._id)}
          className="bg-red-600 text-white px-4 py-2 font-bold hover:bg-red-700 transition-colors"
        >
          删除
        </button>
      </div>
    </div>
  )
}

// Location Card Component
function LocationCard({
  location,
  onEdit,
  onDelete
}: {
  location: Location
  onEdit: (location: Location) => void
  onDelete: (id: string) => Promise<void>
}) {
  return (
    <div className="border-4 border-black overflow-hidden">
      <div className="w-full h-48 overflow-hidden">
        <img
          src={location.imageUrl}
          alt={location.name}
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="p-4">
        <h3 className="text-lg font-bold mb-2">{location.name}</h3>
        <div className="text-sm text-gray-600 mb-4">
          <div>年份: {location.year}</div>
          {location.description && <div>描述: {location.description}</div>}
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(location)}
            className="bg-black text-white px-4 py-2 font-bold hover:bg-gray-800 transition-colors"
          >
            编辑
          </button>
          <button
            onClick={() => location._id && onDelete(location._id)}
            className="bg-red-600 text-white px-4 py-2 font-bold hover:bg-red-700 transition-colors"
          >
            删除
          </button>
        </div>
      </div>
    </div>
  )
}

// Project Edit Modal
function ProjectEditModal({
  project,
  onSave,
  onCancel,
  onFileUpload,
  uploading
}: {
  project: Project | null
  onSave: (project: Project) => Promise<void>
  onCancel: () => void
  onFileUpload: (files: FileList | null) => Promise<string | null>
  uploading: boolean
}) {
  const [formData, setFormData] = useState<Project>(
    project || {
      name: '',
      description: '',
      logoUrl: '',
      websiteUrl: '',
      resourceUrl: '',
      status: 'active',
      displayOrder: 1
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSave(formData)
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileUrl = await onFileUpload(e.target.files)
    if (fileUrl) {
      setFormData({ ...formData, logoUrl: fileUrl })
    }
  }

  // 过往项目（inactive）与进行中项目分开管理，不再通过状态下拉跨组切换
  const isPrevious = formData.status === 'inactive'
  const groupLabel = isPrevious ? '过往项目' : '进行中项目'

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border-4 border-black max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-black">
              {project?._id ? `编辑${groupLabel}` : `添加${groupLabel}`}
            </h2>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-lg font-bold mb-2">项目名称 *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full p-3 border-2 border-black text-lg"
                required
              />
            </div>

            <div>
              <label className="block text-lg font-bold mb-2">项目描述 *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-3 border-2 border-black text-lg h-24 resize-none"
                required
              />
            </div>

            <div>
              <label className="block text-lg font-bold mb-2">备注</label>
              <textarea
                value={formData.note || ''}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                className="w-full p-3 border-2 border-black text-lg h-20 resize-none"
                placeholder="可选的备注文本，将显示在项目描述下方..."
              />
            </div>

            <div>
              <label className="block text-lg font-bold mb-2">Logo 图片</label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="logo-upload"
                  className={`flex items-center space-x-2 px-4 py-2 border-2 border-black cursor-pointer hover:bg-gray-100 ${
                    uploading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Upload className="w-5 h-5" />
                  <span>{uploading ? '上传中...' : '上传图片'}</span>
                </label>
                {formData.logoUrl && (
                  <img
                    src={formData.logoUrl}
                    alt="Logo preview"
                    className="w-16 h-16 object-cover border-2 border-black"
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-lg font-bold mb-2">官网链接 *</label>
              <input
                type="url"
                value={formData.websiteUrl}
                onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                className="w-full p-3 border-2 border-black text-lg"
                required
              />
            </div>

            <div>
              <label className="block text-lg font-bold mb-2">资料链接</label>
              <input
                type="url"
                value={formData.resourceUrl}
                onChange={(e) => setFormData({ ...formData, resourceUrl: e.target.value })}
                className="w-full p-3 border-2 border-black text-lg"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-lg font-bold mb-2">状态 *</label>
                {isPrevious ? (
                  <div className="w-full p-3 border-2 border-black text-lg bg-gray-100 text-gray-600">
                    过往项目
                  </div>
                ) : (
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as Project['status'] })}
                    className="w-full p-3 border-2 border-black text-lg"
                    required
                  >
                    <option value="active">进行中</option>
                    <option value="completed">已完成</option>
                  </select>
                )}
              </div>

              <div>
                <label className="block text-lg font-bold mb-2">显示顺序 *</label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                  className="w-full p-3 border-2 border-black text-lg"
                  required
                  min="1"
                />
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                className="flex items-center space-x-2 bg-black text-white px-6 py-3 font-bold hover:bg-gray-800 transition-colors"
              >
                <Save className="w-5 h-5" />
                <span>保存</span>
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 border-2 border-black font-bold hover:bg-gray-100 transition-colors"
              >
                取消
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// Location Edit Modal
function LocationEditModal({
  location,
  onSave,
  onCancel,
  onFileUpload,
  uploading
}: {
  location: Location | null
  onSave: (location: Location) => Promise<void>
  onCancel: () => void
  onFileUpload: (files: FileList | null) => Promise<string | null>
  uploading: boolean
}) {
  const [formData, setFormData] = useState<Location>(
    location || {
      name: '',
      year: new Date().getFullYear(),
      imageUrl: '',
      description: ''
    }
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSave(formData)
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileUrl = await onFileUpload(e.target.files)
    if (fileUrl) {
      setFormData({ ...formData, imageUrl: fileUrl })
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border-4 border-black max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-black">
              {location?._id ? '编辑地点' : '添加地点'}
            </h2>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-lg font-bold mb-2">地点名称 *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full p-3 border-2 border-black text-lg"
                required
              />
            </div>

            <div>
              <label className="block text-lg font-bold mb-2">旅行年份 *</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                className="w-full p-3 border-2 border-black text-lg"
                required
                min="2000"
                max="2030"
              />
            </div>

            <div>
              <label className="block text-lg font-bold mb-2">地点图片 *</label>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="image-upload"
                    className={`flex items-center space-x-2 px-4 py-2 border-2 border-black cursor-pointer hover:bg-gray-100 ${
                      uploading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Upload className="w-5 h-5" />
                    <span>{uploading ? '上传中...' : '上传图片'}</span>
                  </label>
                </div>
                {formData.imageUrl && (
                  <img
                    src={formData.imageUrl}
                    alt="地点预览"
                    className="w-full h-48 object-cover border-2 border-black"
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-lg font-bold mb-2">地点描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-3 border-2 border-black text-lg h-24 resize-none"
                placeholder="可选的地点描述..."
              />
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                className="flex items-center space-x-2 bg-black text-white px-6 py-3 font-bold hover:bg-gray-800 transition-colors"
                disabled={!formData.imageUrl}
              >
                <Save className="w-5 h-5" />
                <span>保存</span>
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 border-2 border-black font-bold hover:bg-gray-100 transition-colors"
              >
                取消
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
